import { z } from "zod";
import { stepTypeKey } from "../../drizzle/stepTypes.js";
import { taskChannel } from "../../drizzle/taskTypes.js";
import { attachmentCategoryWithContainersSchema, documentCategory } from "../../workflows/document-categories.js";
import { createEmailTaskSchema } from "../../workflows/email-tasks.js";
import { CLAIMINI_ID_ENTITY_NAME, attachmentSchema, findHighestConfidenceAttachmentByCategory, findHighestConfidenceEntity, } from "../../workflows/heidelbot-entities.js";
import { costEstimatePdrTaskSchema } from "../../workflows/pdr-tasks.js";
import { withStepMetadata } from "./step-metadata.js";
/**
 * Email-channel cost estimate data - the Heidelbot email contract with attachments defaulting to [].
 * The backend stores it as step input:
 *   input: { ...data, channel: "email", previousStepId: systemStep.id }
 */
const costEstimateEmailDataSchema = createEmailTaskSchema.omit({ type: true }).extend({
    channel: z.literal(taskChannel.EMAIL),
    attachments: z.array(attachmentSchema).default([]),
});
/** Cost estimate data for the pdr.cloud channel - the pdr contract's cost-estimate arm. */
const costEstimatePdrDataSchema = costEstimatePdrTaskSchema.omit({ type: true }).extend({
    channel: z.literal(taskChannel.PDR),
});
/** Cost estimate data schema, discriminated on `channel`. */
export const costEstimateDataSchema = z.discriminatedUnion("channel", [costEstimateEmailDataSchema, costEstimatePdrDataSchema]);
/** Cost Estimate Data as the cost_estimate_check step receives it, one metadata-wrapped arm per channel. */
export const costEstimateDataWithMetadataSchema = z.discriminatedUnion("channel", [
    withStepMetadata(costEstimateEmailDataSchema),
    withStepMetadata(costEstimatePdrDataSchema),
]);
/**
 * User-verified cost estimate values - typed form output.
 * Reduced to 8 fields matching current business requirements.
 */
export const costEstimateVerifiedValuesSchema = z.object({
    claiminiId: z.string(),
    garage: z.string(),
    // Costs (stored in cents for precision)
    totalCosts: z.int(),
    wage: z.int(),
    materialCosts: z.int(),
    paintWork: z.int(),
    // User decision (not AI-extracted)
    riskOfConsequentialDamage: z.boolean(),
    // Optional notes from reviewer
    notes: z.string().optional(),
    // Whole km from the report. Optional because older records lack it.
    mileage: z.int().nonnegative("Der Kilometerstand darf nicht negativ sein").optional(),
    // Per-attachment user-selected document categories (forwarded to document-manager).
    // Defaulted for backward compatibility: records persisted before this field was
    // introduced lack it, and several read-back paths safeParse them.
    attachmentCategories: z.array(attachmentCategoryWithContainersSchema).default([]),
});
/** Entity names extracted by Heidelbot for the cost-estimate workflow. */
export const costEstimateEntityNames = {
    CLAIMINI_ID: CLAIMINI_ID_ENTITY_NAME,
    GARAGE: "garage",
    TOTAL_COSTS: "totalCosts",
    WAGE: "wage",
    MATERIAL_COSTS: "materialCosts",
    PAINT_WORK: "paintWork",
    MILEAGE: "mileage",
};
/**
 * The workshop name of a pdr.cloud cost estimate: the highest-confidence `garage` entity with a
 * non-blank `displayName` on the attachment with the highest `COST_ESTIMATE` confidence, wherever
 * it sits in the array. `undefined` when there is no such attachment or entity.
 */
export function pdrGarageDisplayName(data) {
    const costEstimate = findHighestConfidenceAttachmentByCategory(data.attachments, documentCategory.COST_ESTIMATE);
    const namedEntities = (costEstimate?.entities ?? []).filter((entity) => (entity.displayName ?? "").trim() !== "");
    return findHighestConfidenceEntity(namedEntities, costEstimateEntityNames.GARAGE)?.displayName?.trim();
}
/**
 * Cost Estimate checked data - result of user review
 * Contains both original AI data and user-verified values
 */
export const costEstimateCheckedDataSchema = z.object({
    // Original AI-extracted data (for reference/auditing)
    originalData: costEstimateDataWithMetadataSchema,
    // User-verified values (typed form output)
    verifiedValues: costEstimateVerifiedValuesSchema,
    // Review metadata
    reviewedAt: z.iso.datetime(),
});
/** Heidelbot's answer to the garage on pdr.cloud, one per decision button. */
export const decisionToGarageAnswer = { ACCEPTED: "accepted", DECLINED: "declined", ON_HOLD: "on-hold" };
/** Heidelbot rejects longer messages. */
export const DECISION_TO_GARAGE_COMMENT_MAX_LENGTH = 2000;
/** The answer each decision button sends to pdr.cloud. */
export const decisionToGarageAnswerByStepType = {
    [stepTypeKey.COST_ESTIMATE_RELEASE]: decisionToGarageAnswer.ACCEPTED,
    [stepTypeKey.COST_ESTIMATE_REJECT]: decisionToGarageAnswer.DECLINED,
    [stepTypeKey.COST_ESTIMATE_COMMISSION_APPRAISAL]: decisionToGarageAnswer.ON_HOLD,
};
/** The workshop needs a reason for `declined` and `on-hold`. */
export function isDecisionToGarageCommentRequired(answer) {
    return answer !== decisionToGarageAnswer.ACCEPTED;
}
/** Whether the Decision to Garage reached pdr.cloud. `unknown`: Heidelbot cannot tell. */
export const decisionToGarageOutcome = { SUCCESS: "success", FAILED: "failed", UNKNOWN: "unknown" };
const decisionToGarageOutcomeSchema = z.enum(decisionToGarageOutcome);
/** The Decision to Garage as sent to Heidelbot once, with the outcome of that one send. */
const decisionToGarageSchema = z.object({
    answer: z.enum(decisionToGarageAnswer),
    answerComment: z.string().nullable(),
    sentAt: z.iso.datetime(),
    outcome: decisionToGarageOutcomeSchema,
});
/** The Decision to Garage outcome in the transition response. */
export const decisionToGarageResultSchema = z.object({ outcome: decisionToGarageOutcomeSchema });
/** Cost Estimate checked data as the follow-up step receives it. Only pdr.cloud decision branches carry `decisionToGarage`. */
export const costEstimateCheckedDataWithMetadataSchema = withStepMetadata(costEstimateCheckedDataSchema.extend({ decisionToGarage: decisionToGarageSchema.optional() }));
/**
 * The reviewer's decision on the docs-only path: the incident id plus the per-attachment
 * categories (no claim decision, no verified field values).
 */
export const costEstimateDocumentsOnlyVerifiedValuesSchema = z.object({
    claiminiId: z.string().min(1),
    // At least one attachment — linking documents is the sole purpose of this path, so an
    // empty list would create a no-op step. The UI already disables the button at zero.
    attachmentCategories: z.array(attachmentCategoryWithContainersSchema).min(1),
});
/**
 * Documents-only checked data — the reduced result the check step produces
 * when the reviewer only links the email's attachments to the existing
 * ContainerEstimate (no claim decision, no verified field values, no section).
 * Only the incident id and the per-attachment categories are carried; the rest of
 * the check step form is intentionally ignored on this path.
 */
export const costEstimateDocumentsOnlyCheckedDataSchema = z.object({
    originalData: costEstimateDataWithMetadataSchema,
    verifiedValues: costEstimateDocumentsOnlyVerifiedValuesSchema,
    reviewedAt: z.iso.datetime(),
});
/** Documents-only step input — the check step's result, as every follow-up step receives it. */
export const costEstimateDocumentsOnlyInputSchema = withStepMetadata(costEstimateDocumentsOnlyCheckedDataSchema);
/**
 * Follow-up step result schemas - one per branch, all unconstrained: the user's decision is
 * recorded via the step type itself. Matches the damage-appraisal pattern.
 */
export const costEstimateReleaseResultSchema = z.unknown();
export const costEstimateCommissionAppraisalResultSchema = z.unknown();
export const costEstimateRejectResultSchema = z.unknown();
export const costEstimateDocumentsOnlyResultSchema = z.unknown();
